import { Controller } from "@hotwired/stimulus"

// Upload step of the LV import wizard: drag&drop dropzone for the native
// direct-upload file field, source-type auto-detection from the file
// extension, file chip and submit gating. The actual upload still runs through
// Rails' DirectUpload on form submit — this controller is purely presentational.
export default class extends Controller {
  static targets = [
    "dropzone",
    "input",
    "sourceRadio",
    "fileChip",
    "fileName",
    "fileSize",
    "submit",
    "mismatchHint",
    "progressWrap",
    "progressBar",
  ]

  static values = {
    maxSize: { type: Number, default: 26214400 }, // 25MB, matches the model validation
    mismatchMessage: String,
    fileTooLargeMessage: String,
  }

  static EXTENSION_PATTERNS = {
    gaeb: /\.(x8[0-6]|gaeb|xml)$/i,
    spreadsheet: /\.(csv|xlsx|xls)$/i,
    ai_pdf: /\.pdf$/i,
  }

  connect() {
    this.syncAccept()
    this.updateSubmitState()
  }

  // --- Drag & drop ---

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-green-500", "bg-green-50")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-green-500", "bg-green-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-green-500", "bg-green-50")

    const file = event.dataTransfer.files[0]
    if (!file) return

    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.inputTarget.files = transfer.files
    this.fileSelected()
  }

  browse() {
    this.inputTarget.click()
  }

  // --- File selection ---

  fileSelected() {
    const file = this.inputTarget.files[0]
    if (!file) {
      this.clearFile()
      return
    }

    if (file.size > this.maxSizeValue) {
      this.inputTarget.value = null
      this.showMismatchHint(this.fileTooLargeMessageValue)
      this.updateSubmitState()
      return
    }

    this.autoDetectSource(file.name)
    this.fileNameTarget.textContent = file.name
    this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    this.fileChipTarget.classList.remove("hidden")
    this.dropzoneTarget.classList.add("hidden")
    this.checkMismatch(file.name)
    this.updateSubmitState()
  }

  removeFile(event) {
    event.preventDefault()
    event.stopPropagation()
    this.clearFile()
  }

  clearFile() {
    this.inputTarget.value = null
    this.fileChipTarget.classList.add("hidden")
    this.dropzoneTarget.classList.remove("hidden")
    this.hideMismatchHint()
    this.updateSubmitState()
  }

  // --- Source type ---

  sourceChanged() {
    this.syncAccept()
    const file = this.inputTarget.files[0]
    if (file) this.checkMismatch(file.name)
  }

  autoDetectSource(filename) {
    const detected = this.detectSource(filename)
    if (!detected) return

    this.sourceRadioTargets.forEach((radio) => {
      radio.checked = radio.value === detected
    })
    this.syncAccept()
  }

  detectSource(filename) {
    const patterns = this.constructor.EXTENSION_PATTERNS
    return Object.keys(patterns).find((source) => patterns[source].test(filename)) || null
  }

  selectedSource() {
    const checked = this.sourceRadioTargets.find((radio) => radio.checked)
    return checked ? checked.value : null
  }

  syncAccept() {
    const checked = this.sourceRadioTargets.find((radio) => radio.checked)
    if (checked && checked.dataset.accept)
      this.inputTarget.setAttribute("accept", checked.dataset.accept)
  }

  checkMismatch(filename) {
    const detected = this.detectSource(filename)
    const selected = this.selectedSource()
    if (detected && selected && detected !== selected) {
      this.showMismatchHint(this.mismatchMessageValue)
    } else {
      this.hideMismatchHint()
    }
  }

  showMismatchHint(message) {
    if (!this.hasMismatchHintTarget) return
    this.mismatchHintTarget.textContent = message || ""
    this.mismatchHintTarget.classList.remove("hidden")
  }

  hideMismatchHint() {
    if (!this.hasMismatchHintTarget) return
    this.mismatchHintTarget.classList.add("hidden")
  }

  // --- Submit + direct upload progress ---

  updateSubmitState() {
    this.submitTarget.disabled = !this.inputTarget.files[0]
  }

  submitStart() {
    this.submitTarget.disabled = true
    if (this.hasProgressWrapTarget) this.progressWrapTarget.classList.remove("hidden")
  }

  uploadProgress(event) {
    if (!this.hasProgressBarTarget) return
    const { progress } = event.detail
    this.progressBarTarget.style.width = `${progress}%`
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }
}
